import { Modal, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { CountByKeyDto } from 'src/types/cartography.types';

import styles from './SummaryDetailModal.module.scss';
import { Props } from './SummaryDetailModal.types';

const SummaryDetailModal = ({ metric, summary, onClose }: Props) => {
  const { t } = useTranslation();

  const title = metric
    ? {
        bedrooms: t('admin.structure.metrics.bedrooms', 'Chambres'),
        beds: t('admin.structure.metrics.beds', 'Lits'),
        bathrooms: t('admin.structure.metrics.bathrooms', "Salles de bain et salles d'eau"),
        capacity: t('admin.structure.metrics.capacity', "Capacité d'accueil"),
      }[metric]
    : '';

  const Section = ({ label, rows }: { label: string; rows: { key: string; count: number }[] }) => {
    if (rows.length === 0) return null;
    return (
      <div className={styles.section}>
        <span className={styles.sectionTitle}>{label}</span>
        {rows.map(row => (
          <div key={row.key} className={styles.row}>
            <span>{row.key}</span>
            <span>{row.count}</span>
          </div>
        ))}
      </div>
    );
  };

  /** Les clés renvoyées par l'API sont des enums : on les traduit à l'affichage. */
  const translateKeys = (rows: CountByKeyDto[], prefix: string): CountByKeyDto[] =>
    rows.map(row => ({ ...row, key: t(`${prefix}.${row.key}`, row.key) }));

  const renderBody = () => {
    if (!summary || !metric) return null;

    if (metric === 'bedrooms') {
      return (
        <>
          <Text size="sm">
            {t('admin.structure.metrics.bedroomsDetail', {
              double: summary.sleepingRooms.withDoubleBed,
              single: summary.sleepingRooms.withSingleBedsOnly,
              empty: summary.sleepingRooms.withoutBed,
              defaultValue:
                '{{double}} avec lit double · {{single}} en lits simples · {{empty}} sans couchage',
            })}
          </Text>
          <Section
            label={t('admin.structure.metrics.byFloor', 'Par niveau')}
            rows={summary.sleepingRooms.byFloor}
          />
          <Section
            label={t('admin.structure.metrics.byBuilding', 'Par bâtiment')}
            rows={summary.sleepingRooms.byBuilding}
          />
          <Section
            label={t('admin.structure.metrics.byType', 'Par type')}
            rows={translateKeys(summary.sleepingRooms.byType, 'admin.structure.roomTypes')}
          />
        </>
      );
    }

    if (metric === 'beds') {
      return (
        <>
          <Section
            label={t('admin.structure.metrics.byType', 'Par type')}
            rows={translateKeys(summary.beds.byType, 'admin.structure.bedTypes')}
          />
          <Section
            label={t('admin.structure.metrics.byWidth', 'Par largeur de couchage')}
            rows={summary.beds.byWidthCm.map(row => ({ ...row, key: `${row.key} cm` }))}
          />
        </>
      );
    }

    if (metric === 'bathrooms') {
      return (
        <>
          <Text size="sm">
            {t('admin.structure.metrics.enSuiteDetail', {
              count: summary.washrooms.enSuite,
              defaultValue: '{{count}} attenante(s) à une chambre',
            })}
          </Text>
          <Section
            label={t('admin.structure.metrics.byType', 'Par type')}
            rows={translateKeys(summary.washrooms.byType, 'admin.structure.roomTypes')}
          />
          <Section
            label={t('admin.structure.metrics.byFeature', 'Équipements (toutes pièces)')}
            rows={translateKeys(summary.washrooms.byFeature, 'admin.structure.roomFeatures')}
          />
        </>
      );
    }

    return (
      <Section
        label={t('admin.structure.metrics.byBuilding', 'Par bâtiment')}
        rows={summary.capacityByBuilding.map(entry => ({
          key: entry.name,
          count: entry.capacity,
        }))}
      />
    );
  };

  return (
    <Modal opened={metric !== null} onClose={onClose} title={title} centered>
      <div className={styles.container}>{renderBody()}</div>
    </Modal>
  );
};

export default SummaryDetailModal;
